import { IsoDate, IsoDateTime, IsoDateTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate, DeconstructedDuration } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';

export function subtractDate<T extends IsoDate | IsoDateTime | IsoDateTimeTz>(a: T, b: T): IsoDuration {
  const da = deconstructDate(a);
  const db = deconstructDate(b);

  // Convert to UTC Date objects
  let dateA: Date, dateB: Date;
  if (da._type === 'date') {
    dateA = new Date(Date.UTC(da.year, da.month - 1, da.day));
  } else if (da._type === 'dateTime') {
    dateA = new Date(Date.UTC(da.year, da.month - 1, da.day, da.hours, da.minutes, da.seconds, da.milliseconds));
  } else if (da._type === 'dateTimeTz') {
    let offsetMinutes = 0;
    if (da.offset !== 'Z') {
      offsetMinutes = (da.offset.offsetHours * 60 + da.offset.offsetMinutes) * (da.offset.offsetSign === '-' ? -1 : 1);
    }
    dateA = new Date(Date.UTC(da.year, da.month - 1, da.day, da.hours, da.minutes, da.seconds, da.milliseconds) - offsetMinutes * 60000);
  } else {
    throw new Error('Unsupported type for a');
  }

  if (db._type === 'date') {
    dateB = new Date(Date.UTC(db.year, db.month - 1, db.day));
  } else if (db._type === 'dateTime') {
    dateB = new Date(Date.UTC(db.year, db.month - 1, db.day, db.hours, db.minutes, db.seconds, db.milliseconds));
  } else if (db._type === 'dateTimeTz') {
    let offsetMinutes = 0;
    if (db.offset !== 'Z') {
      offsetMinutes = (db.offset.offsetHours * 60 + db.offset.offsetMinutes) * (db.offset.offsetSign === '-' ? -1 : 1);
    }
    dateB = new Date(Date.UTC(db.year, db.month - 1, db.day, db.hours, db.minutes, db.seconds, db.milliseconds) - offsetMinutes * 60000);
  } else {
    throw new Error('Unsupported type for b');
  }

  let diffMs = dateA.getTime() - dateB.getTime();
  const abs = Math.abs;
  const sign = diffMs < 0 ? -1 : 1;
  if(sign < 0)
    throw new Error('subtractDateFunction: sign of difference is negative, a must be greater than b');
  diffMs = abs(diffMs);

  // Calculate days, hours, minutes, seconds, milliseconds
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  diffMs -= days * 24 * 60 * 60 * 1000;
  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  diffMs -= hours * 60 * 60 * 1000;
  const minutes = Math.floor(diffMs / (60 * 1000));
  diffMs -= minutes * 60 * 1000;
  const seconds = Math.floor(diffMs / 1000);
  diffMs -= seconds * 1000;
  const milliseconds = diffMs;

  // Build duration object
  const duration: DeconstructedDuration = {
    _type: 'duration',
    years: 0,
    months: 0,
    days: days,
    hours: hours,
    minutes: minutes,
    seconds: seconds,
    milliseconds: milliseconds,
  };
  return constructDate(duration) as IsoDuration;
}
